import { ICurrencyResp } from '@monetize/types/app';
import { MdDeleteOutline, MdLock } from 'react-icons/md';
import {
  useCreateOrUpdateCurrencySettings,
  useDeleteCurrencySettings,
} from '../../../../api/settingsService';
import {
  MBox,
  MButton,
  MCustomIconButton,
  MFlex,
  MTag,
  MTagLabel,
  MTagLeftIcon,
  MText,
  MTooltip,
} from '../../../../components/Monetize';
import { useDashboardCurrencyFilter } from '../../../Dashboard/components/useDashboardCurrencyFilter';
import {
  getCurrencyName,
  getCurrencySample,
} from '../utils/currencySettings.utils';

const LOCKED_TOOLTIP =
  "This currency can't be removed because products, invoices, or transactions use it.";

interface EnabledCurrencyRowProps {
  currency: ICurrencyResp;
  isSettingDefaultDisabled: boolean;
  onSettingDefaultChange: (isPending: boolean) => void;
}

export const EnabledCurrencyRow = ({
  currency,
  isSettingDefaultDisabled,
  onSettingDefaultChange,
}: EnabledCurrencyRowProps) => {
  const { code, locked, defaultCurrency } = currency;
  const { setCurrency: setDashboardCurrencyFilter } =
    useDashboardCurrencyFilter();

  const { mutate: doSetDefault, isPending: isSettingDefault } =
    useCreateOrUpdateCurrencySettings({
      meta: { showErrorToast: true },
      onMutate: () => onSettingDefaultChange(true),
      onSuccess: () => setDashboardCurrencyFilter(code),
      onSettled: () => onSettingDefaultChange(false),
    });

  const { mutate: doDelete, isPending: isDeleting } = useDeleteCurrencySettings(
    { meta: { showErrorToast: true } },
  );

  return (
    <MFlex
      data-testid={`currency-row-${code}`}
      align="center"
      gap={3}
      px={4}
      py={3}
      minH={14}
      _notFirst={{
        borderTopWidth: '1px',
        borderTopStyle: 'solid',
        borderTopColor: 'border.subtle',
      }}
    >
      <MText w={12} flexShrink={0} fontWeight="semibold" color="fg.strong">
        {code}
      </MText>
      <MFlex
        flex={1}
        minW={0}
        align="center"
        flexWrap="wrap"
        columnGap={2}
        rowGap={1}
      >
        <MText truncate color="fg.primary">
          {getCurrencyName(code)}
        </MText>
        {defaultCurrency && (
          <MTag variant="blue" flexShrink={0}>
            Default
          </MTag>
        )}
        {locked && (
          <MTooltip label={LOCKED_TOOLTIP} placement="top">
            <MTag variant="gray" flexShrink={0} tabIndex={0}>
              <MTagLeftIcon as={MdLock} boxSize={3} mr={1} />
              <MTagLabel>In use</MTagLabel>
            </MTag>
          </MTooltip>
        )}
      </MFlex>
      <MText
        display={{ base: 'none', md: 'block' }}
        color="fg.muted"
        fontSize="sm"
        textAlign="right"
        css={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {getCurrencySample(code)}
      </MText>
      <MFlex
        w={{ base: 'auto', md: 40 }}
        flexShrink={0}
        justify="flex-end"
        align="center"
        gap={1}
      >
        {!defaultCurrency && (
          <MButton
            data-testid={`currency-make-default-${code}`}
            variant="tertiary"
            size="sm"
            isLoading={isSettingDefault}
            isDisabled={isSettingDefaultDisabled || isDeleting}
            onClick={() =>
              doSetDefault({
                action: 'update',
                payload: { ...currency, defaultCurrency: true },
              })
            }
          >
            Make default
          </MButton>
        )}
        {/* Keeps the action column aligned whether or not the remove button renders */}
        <MBox w={8} display="flex" justifyContent="center">
          {!defaultCurrency && !locked && (
            <MCustomIconButton
              data-testid={`currency-remove-${code}`}
              aria-label={`Remove ${code}`}
              variant="icon"
              icon={MdDeleteOutline}
              btnSize={5}
              containerSize={8}
              isLoading={isDeleting}
              isDisabled={isSettingDefault}
              onClick={() => doDelete({ id: currency.id })}
            />
          )}
        </MBox>
      </MFlex>
    </MFlex>
  );
};
